import { useEffect, useState, useRef } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  Timestamp,
  doc,
  getDoc,
  addDoc,
  limit,
  getDocs
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';

interface MessengerNotification {
  id: string;
  conversationId: string;
  userName: string;
  userProfilePic: string;
  message: string;
  timestamp: Date;
}

export const useMessengerNotifications = () => {
  const [notification, setNotification] = useState<MessengerNotification | null>(null);
  const { user } = useAuth();
  const lastMessageIdRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!user?.uid) return;

    // Récupérer le restaurantId de l'utilisateur
    const getUserProfile = async () => {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      if (!userData?.restaurantId) return;
      const restaurantId = userData.restaurantId;

      // Initialiser le dernier message ID pour éviter les notifications au chargement initial
      const conversationsRef = collection(
        db,
        'restaurants',
        restaurantId,
        'messenger_conversations'
      );

      const conversationsQuery = query(
        conversationsRef,
        orderBy('lastUpdated', 'desc')
      );

      // Premier snapshot pour initialiser sans notifications
      const initialQuery = query(conversationsRef, orderBy('lastUpdated', 'desc'), limit(1));
      const initialSnapshot = await getDocs(initialQuery);
      if (!initialSnapshot.empty) {
        const latestConv = initialSnapshot.docs[0].data();
        if (latestConv.lastMessage?.mid) {
          lastMessageIdRef.current = latestConv.lastMessage.mid;
        }
      }

      const unsubscribe = onSnapshot(
        conversationsQuery,
        { includeMetadataChanges: false },
        async (snapshot) => {
          for (const change of snapshot.docChanges()) {
            if (change.type === 'modified') { // Seulement les modifications, pas les ajouts initiaux
              const conversationData = change.doc.data();
              const lastMessage = conversationData.lastMessage;
              
              // Vérifier si c'est un nouveau message utilisateur avec données valides
              if (
                lastMessage &&
                lastMessage.sender === 'user' &&
                lastMessage.mid !== lastMessageIdRef.current &&
                conversationData.unreadCount > 0 &&
                conversationData.userName &&
                conversationData.userName !== 'Test User' &&
                lastMessage.text &&
                lastMessage.timestamp
              ) {
                lastMessageIdRef.current = lastMessage.mid;
                
                // Créer seulement la notification popup (pas dans la cloche pour éviter les doublons)
                setNotification({
                  id: `notif_${Date.now()}`,
                  conversationId: change.doc.id,
                  userName: conversationData.userName,
                  userProfilePic: conversationData.userProfilePic || '',
                  message: lastMessage.text,
                  timestamp: lastMessage.timestamp.toDate()
                });

                // Jouer un son de notification
                try {
                  const audio = new Audio('/notification.mp3');
                  audio.play().catch(() => {});
                } catch (e) {
                  // Silencieux si le son n'est pas disponible
                }
              }
            }
          }
        },
        (error) => {
          console.error('Erreur listener notifications Messenger:', error);
        }
      );

      unsubscribeRef.current = unsubscribe;
    };

    getUserProfile();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [user]);

  const clearNotification = () => {
    setNotification(null);
  };

  return {
    notification,
    clearNotification
  };
};
